// Terms and conditions for each site version. Blocks inside a section are
// either a plain string (a paragraph) or one of:
//   { type: "h3", text }                      subsection heading
//   { type: "ol" | "ul", items }              numbered or bulleted list
//   { type: "fields", items: [{ label, value, href? }] }
const SUPPORT_EMAIL = "hossein@fatemi.net";

export const terms = {
  en: {
    labels: {
      eyebrow: "Legal",
      contents: "Contents",
    },
    title: "PocketPlay – Terms of Service",
    intro: [
      "Welcome to PocketPlay!",
      "PocketPlay is an online gaming platform designed to bring people together through fun, interactive, and multiplayer games.",
      "Our platform allows players to enjoy games directly through supported web browsers, using smartphones as controllers and a shared screen for an engaging group gaming experience.",
      "These Terms of Service (\"Terms\") govern your access to and use of the PocketPlay website, games, subscriptions, and related services.",
      "By creating an account, purchasing a game or subscription, or using our services, you agree to these Terms.",
      "Please read them carefully before making a purchase.",
    ],
    sections: [
      {
        id: "about",
        title: "1. About PocketPlay",
        blocks: [
          "PocketPlay provides browser-based games, multiplayer entertainment experiences, subscription plans, and individual game purchases.",
          "The service is operated by:",
          {
            type: "fields",
            items: [
              { label: "Legal Business Name", value: "[Registered Business Name]" },
              { label: "Business Address", value: "[Business Address]" },
              { label: "Contact Email", value: SUPPORT_EMAIL, href: `mailto:${SUPPORT_EMAIL}` },
              { label: "Website", value: "https://pocketplay.games/", href: "https://pocketplay.games/" },
            ],
          },
          "Throughout these Terms, \"PocketPlay,\" \"we,\" \"us,\" and \"our\" refer to the operator identified above.",
        ],
      },
      {
        id: "accounts",
        title: "2. Accounts and Eligibility",
        blocks: [
          "To access certain features, users may need to create an account.",
          "By registering, you agree to:",
          {
            type: "ul",
            items: [
              "Provide accurate and current account information.",
              "Maintain the confidentiality of your login credentials.",
              "Notify us if you suspect unauthorized access to your account.",
              "Use the platform in accordance with these Terms and applicable laws.",
            ],
          },
          "You are responsible for activities conducted through your account to the extent permitted by law.",
          "Users under the age of majority in their jurisdiction must have permission from a parent or legal guardian to make purchases or use paid services.",
          "Parents and guardians are responsible for supervising minors' use of the platform.",
        ],
      },
      {
        id: "services",
        title: "3. Games and Available Services",
        blocks: [
          "PocketPlay offers different ways to access games.",
          { type: "h3", text: "3.1. Game Night Pass" },
          "The Game Night Pass provides access to games included in the plan for a period of 24 hours.",
          "The activation time, expiration time, price, and included features are displayed before purchase.",
          "Unless expressly stated otherwise at checkout, this pass does not automatically renew.",
          { type: "h3", text: "3.2. Gold Subscription" },
          "The Gold subscription provides access to the games included in PocketPlay's Gold catalog throughout the active subscription period.",
          "Gold may be offered as a monthly or annual subscription.",
          "The applicable billing period, price, renewal conditions, and included features are displayed before purchase.",
          { type: "h3", text: "3.3. Venue Subscription" },
          "The Venue subscription is designed for businesses, entertainment venues, and organizations that need multiple simultaneous gaming sessions.",
          "It includes Gold subscription benefits with additional concurrent-session capacity.",
          "A Venue plan advertised with a 10-session limit allows up to 10 independent gaming sessions simultaneously.",
          "The exact capacity, usage permissions, billing period, and other plan limitations are shown at checkout.",
          { type: "h3", text: "3.4. Individual Game Purchases" },
          "Users may purchase access to individual games without maintaining an active subscription.",
          "An individually purchased game does not have a predefined access expiration date.",
          "The purchased entitlement remains associated with the user's account and is intended to provide ongoing access without additional subscription fees.",
          "Individual purchases grant a license to access and play the relevant game through PocketPlay. They do not transfer ownership of the game's software, source code, or intellectual property.",
          "Because these games are delivered through an online service, their operation depends on the continued availability of the necessary platform infrastructure.",
          "If a purchased game is permanently discontinued or access can no longer be provided, PocketPlay will address affected purchases in accordance with its contractual commitments and applicable consumer protection laws.",
        ],
      },
      {
        id: "pricing",
        title: "4. Pricing, Payments, and Taxes",
        blocks: [
          "All applicable prices are displayed before purchase.",
          "Prices may vary based on the user's region, selected plan, currency, or promotional offers.",
          "Where applicable, taxes and additional mandatory charges will be disclosed before the purchase is completed.",
          "Payments may be processed through authorized third-party payment providers, including Stripe.",
          "By submitting payment information, you authorize the applicable payment provider to process the transaction.",
          "PocketPlay does not require users to disclose their complete payment card information directly to our support team.",
          "We may update prices for future purchases or subscription periods.",
          "For recurring subscriptions, price changes will be communicated in advance as required by applicable law.",
        ],
      },
      {
        id: "renewal",
        title: "5. Subscription Billing and Automatic Renewal",
        blocks: [
          "Some PocketPlay subscription plans may automatically renew.",
          "For plans with automatic renewal:",
          {
            type: "ol",
            items: [
              "The recurring price and billing frequency will be clearly disclosed before purchase.",
              "By expressly accepting the recurring billing arrangement, you authorize the applicable recurring charges.",
              "The subscription renews according to the billing schedule disclosed at checkout unless canceled or otherwise terminated.",
              "You may cancel automatic renewal through the available subscription management options or by contacting support.",
              "Cancellation must be processed in accordance with applicable consumer protection requirements.",
              "Any required renewal reminders, cancellation rights, and refund entitlements will be provided in accordance with applicable law.",
            ],
          },
          "When you cancel a future renewal before the renewal date, access will ordinarily continue through the already-paid subscription period.",
          "Where applicable law provides an earlier termination right or a refund following renewal, those rights will be honored.",
          "Non-renewing plans and individual game purchases are not subject to recurring charges unless a separate recurring payment arrangement is explicitly accepted.",
        ],
      },
      {
        id: "activation",
        title: "6. Access and Service Activation",
        blocks: [
          "Digital purchases are generally activated after successful payment confirmation.",
          "Access to purchased games and subscriptions is linked to the user's PocketPlay account.",
          "Users are responsible for having compatible devices, supported browsers, and a sufficiently reliable internet connection.",
          "Some multiplayer experiences require a shared display and one or more smartphones or other compatible devices.",
          "PocketPlay may modify supported technical configurations as the platform evolves, subject to applicable contractual obligations.",
        ],
      },
      {
        id: "refunds",
        title: "7. Cancellations and Refunds",
        blocks: [
          "We want users to have a clear understanding of their purchase and cancellation options.",
          { type: "h3", text: "7.1. Subscription Cancellation" },
          "Users may cancel future automatic renewals using the available account management tools or by contacting customer support.",
          "Cancellation of an upcoming renewal does not automatically mean that an already-paid period is refunded.",
          "However, any mandatory cancellation or refund rights under applicable consumer protection laws remain fully protected.",
          { type: "h3", text: "7.2. Digital Purchases and Immediate Access" },
          "Games and subscriptions are digital services that may become available immediately after purchase.",
          "Where permitted by applicable law, users may be asked to expressly consent to immediate access and acknowledge how this affects their statutory cancellation rights.",
          "Any required acknowledgment or consent will be obtained during checkout.",
          "Such consent will not be interpreted as waiving consumer rights that cannot legally be waived.",
          { type: "h3", text: "7.3. Refund Requests" },
          "Refund requests will be reviewed based on the circumstances of the purchase and applicable laws.",
          "Users may be entitled to a refund or another legally required remedy in cases such as:",
          {
            type: "ul",
            items: [
              "Failure to activate a successfully paid purchase.",
              "Significant failure to provide a purchased service.",
              "Duplicate or incorrect charges.",
              "Other circumstances requiring a refund under applicable consumer protection laws.",
            ],
          },
          `For assistance, contact ${SUPPORT_EMAIL} and include the relevant purchase details.`,
          "Approved refunds will ordinarily be issued through the original payment method, subject to payment-provider processing requirements.",
          "Nothing in these Terms excludes or limits mandatory statutory refund rights.",
        ],
      },
      {
        id: "promotions",
        title: "8. Promotions and Referral Programs",
        blocks: [
          "PocketPlay may provide promotional discounts, referral codes, subscription extensions, or other rewards.",
          "Each promotion is subject to its published eligibility requirements, expiration dates, usage restrictions, and applicable conditions.",
          "Referral benefits, when available, are applied according to the referral program terms displayed at the time of the qualifying purchase.",
          "Unless expressly stated otherwise, promotional rewards have no cash value and cannot be exchanged for money.",
          "PocketPlay may reject fraudulent promotional activity or misuse of referral codes, subject to applicable law.",
        ],
      },
      {
        id: "acceptable-use",
        title: "9. Acceptable Use",
        blocks: [
          "You agree to use PocketPlay in a respectful and lawful manner.",
          "The following activities are prohibited:",
          {
            type: "ul",
            items: [
              "Attempting unauthorized access to accounts, games, or servers.",
              "Using cheats, exploits, or malicious software to disrupt gameplay or service operation.",
              "Harassing, threatening, or abusing other players.",
              "Distributing harmful or unlawful content through the platform.",
              "Reselling or redistributing access without authorization.",
              "Attempting to bypass payment requirements or technical restrictions.",
              "Using subscription plans beyond their licensed capacity or permitted purpose.",
            ],
          },
          "PocketPlay may take proportionate action against violations, including restricting or suspending access.",
          "Any such action will be subject to applicable law and the user's contractual rights.",
        ],
      },
      {
        id: "commercial-use",
        title: "10. Personal and Commercial Use",
        blocks: [
          "Standard consumer subscriptions and individual game purchases are intended for personal entertainment unless the applicable product description expressly permits another use.",
          "Commercial venues, entertainment businesses, and organizations must select a plan that permits their intended commercial usage.",
          "Venue subscriptions are designed to support eligible commercial gaming experiences within the purchased session limits.",
          "Purchase of any plan does not grant permission to reproduce, modify, distribute, or independently host PocketPlay games.",
        ],
      },
      {
        id: "intellectual-property",
        title: "11. Intellectual Property",
        blocks: [
          "All rights, title, and interest in the PocketPlay platform, including original software, designs, trademarks, graphics, and other proprietary content, belong to PocketPlay or their respective rights holders.",
          "Subject to these Terms and the applicable purchase conditions, users receive a limited, non-exclusive license to access and use the service.",
          "Users may not copy, reverse engineer, redistribute, or commercially exploit protected platform content except where authorized by law or an applicable license.",
        ],
      },
      {
        id: "privacy",
        title: "12. Privacy and Data Protection",
        blocks: [
          "PocketPlay respects user privacy and aims to protect personal information.",
          "We may process information necessary to operate accounts, provide games, manage purchases, prevent abuse, and deliver customer support.",
          "Personal information will be handled in accordance with our Privacy Policy and applicable data protection laws.",
          "Payment information may be processed by authorized third-party payment providers.",
        ],
      },
      {
        id: "availability",
        title: "13. Service Availability and Updates",
        blocks: [
          "PocketPlay aims to provide reliable and enjoyable gaming experiences.",
          "However, temporary interruptions may occur due to maintenance, technical issues, internet connectivity, or circumstances outside our reasonable control.",
          "We may update games, introduce features, correct bugs, and improve platform performance.",
          "Changes to subscription catalogs or purchased games will be handled in accordance with the commitments made at purchase and applicable consumer rights.",
          "Where a service failure creates a legal entitlement to a remedy, PocketPlay will provide the remedy required by applicable law.",
        ],
      },
      {
        id: "termination",
        title: "14. Account Suspension and Termination",
        blocks: [
          "Users may request account closure by contacting customer support or using available account-management features.",
          "Before closing an account, users should review any active subscriptions and purchased-game entitlements.",
          "Account closure does not automatically cancel a separate recurring payment authorization unless the applicable subscription is also canceled or the law requires cancellation.",
          "PocketPlay may suspend or terminate accounts involved in serious violations, unauthorized activity, or security threats.",
          "Any suspension or termination will be proportionate and consistent with applicable law.",
        ],
      },
      {
        id: "liability",
        title: "15. Disclaimer and Limitation of Liability",
        blocks: [
          "PocketPlay strives to maintain a reliable platform but does not guarantee uninterrupted or error-free operation at all times.",
          "To the extent permitted by applicable law, PocketPlay is not responsible for service interruptions caused solely by the user's internet connection, incompatible devices, or circumstances beyond our reasonable control.",
          "Nothing in these Terms excludes liability that cannot legally be excluded, including liability arising from mandatory consumer guarantees, fraud, or other protected legal rights.",
          "Any limitations of liability will apply only to the extent permitted by the laws governing the relevant purchase.",
        ],
      },
      {
        id: "changes",
        title: "16. Changes to These Terms",
        blocks: [
          "PocketPlay may update these Terms to reflect service improvements, operational changes, or legal requirements.",
          "Where required by law, users will receive advance notice of material changes and any associated cancellation rights.",
          "Changes will not unlawfully remove rights established under existing purchases or subscriptions.",
        ],
      },
      {
        id: "governing-law",
        title: "17. Governing Law and Dispute Resolution",
        blocks: [
          "These Terms are governed by the applicable laws of [Operator's Legal Jurisdiction], subject to any mandatory consumer protections that apply in the user's place of residence.",
          "Users are encouraged to contact PocketPlay support first to seek a resolution to complaints or disputes.",
          "Nothing in these Terms limits a consumer's right to pursue remedies through competent courts, consumer protection authorities, or other legally available dispute resolution mechanisms.",
        ],
      },
      {
        id: "contact",
        title: "18. Contact Information",
        blocks: [
          "For questions, technical support, billing issues, cancellations, or refund requests, please contact:",
          {
            type: "fields",
            items: [
              { label: "Business Name", value: "PocketPlay" },
              { label: "Website", value: "https://pocketplay.games/", href: "https://pocketplay.games/" },
              { label: "Support Email", value: SUPPORT_EMAIL, href: `mailto:${SUPPORT_EMAIL}` },
            ],
          },
        ],
      },
    ],
    closing: [
      "Thank you for choosing PocketPlay.",
      "We hope you enjoy discovering new games, connecting with friends and family, and making memorable moments together.",
    ],
  },
  fa: {
    labels: {
      eyebrow: "قوانین",
      contents: "فهرست مطالب",
    },
    title: "قوانین و مقررات پاکت‌پلی (PocketPlay)",
    intro: [
      "به پاکت‌پلی خوش آمدید!",
      "پاکت‌پلی یک پلتفرم بازی و سرگرمی آنلاین است که امکان اجرای بازی‌های متنوع، گروهی و چندنفره را از طریق مرورگر فراهم می‌کند. کاربران می‌توانند با استفاده از رایانه یا نمایشگر اصلی و گوشی‌های هوشمند به‌عنوان کنترلر، از بازی‌ها لذت ببرند.",
      "استفاده از خدمات پاکت‌پلی و خرید بازی یا اشتراک به معنای مطالعه و پذیرش این قوانین است. لطفاً پیش از خرید، شرایط زیر را مطالعه کنید.",
    ],
    sections: [
      {
        id: "about",
        title: "۱. معرفی کسب‌وکار",
        blocks: [
          "پاکت‌پلی با نام تجاری PocketPlay خدمات بازی آنلاین، اشتراک دسترسی به بازی‌ها و خرید مستقل بازی‌های دیجیتال را ارائه می‌دهد.",
          "اطلاعات صاحب امتیاز و راه‌های ارتباطی رسمی کسب‌وکار در بخش «اطلاعات تماس و پشتیبانی» این صفحه درج شده است.",
        ],
      },
      {
        id: "accounts",
        title: "۲. ثبت‌نام و حساب کاربری",
        blocks: [
          {
            type: "ol",
            items: [
              "کاربران موظف‌اند هنگام ثبت‌نام اطلاعات صحیح و معتبر وارد کنند.",
              "حفظ امنیت اطلاعات ورود و فعالیت‌های انجام‌شده از طریق حساب کاربری بر عهده صاحب حساب است؛ مگر در مواردی که مشکل ناشی از قصور یا نقض تعهدات پاکت‌پلی باشد.",
              "استفاده از حساب دیگران بدون اجازه، ایجاد اختلال در خدمات و تلاش برای دسترسی غیرمجاز به سیستم ممنوع است.",
              "در صورت مشاهده فعالیت غیرمجاز، پاکت‌پلی می‌تواند اقدامات متناسب برای حفاظت از سامانه و کاربران انجام دهد.",
              "استفاده افراد زیر سن قانونی از خدمات پولی باید با رضایت و نظارت والدین یا سرپرست قانونی انجام شود.",
            ],
          },
        ],
      },
      {
        id: "services",
        title: "۳. خدمات و محصولات",
        blocks: [
          "پاکت‌پلی روش‌های مختلفی برای دسترسی به بازی‌ها ارائه می‌دهد:",
          { type: "h3", text: "اشتراک Game Night" },
          "این اشتراک امکان دسترسی به بازی‌های مشمول طرح را به مدت ۲۴ ساعت فراهم می‌کند. زمان شروع و پایان اعتبار در هنگام خرید مشخص می‌شود.",
          { type: "h3", text: "اشتراک Gold" },
          "دارندگان این اشتراک در طول مدت اعتبار می‌توانند به مجموعه بازی‌های مشمول طرح Gold دسترسی داشته باشند.",
          "اشتراک ممکن است در دوره‌های ماهانه یا سالانه عرضه شود. مدت دقیق اعتبار و قیمت پیش از پرداخت نمایش داده می‌شود.",
          { type: "h3", text: "اشتراک Venue" },
          "این اشتراک شامل امکانات Gold به همراه قابلیت برگزاری جلسات بازی هم‌زمان بیشتر است و برای مجموعه‌ها، مراکز تفریحی و مکان‌های تجاری مناسب است.",
          "در طرح Venue با ظرفیت ۱۰ جلسه، حداکثر ۱۰ جلسه مستقل بازی به‌صورت هم‌زمان قابل اجرا خواهد بود.",
          { type: "h3", text: "خرید مستقل بازی" },
          "کاربر می‌تواند دسترسی به بازی‌های مشخص را بدون نیاز به اشتراک فعال خریداری کند.",
          "خرید مستقل بازی دارای تاریخ انقضای ازپیش‌تعیین‌شده نیست و حق دسترسی به بازی خریداری‌شده در حساب کاربر باقی می‌ماند.",
          "این خرید به معنای انتقال مالکیت معنوی یا فایل منبع بازی نیست. دسترسی و اجرای بازی از طریق زیرساخت پاکت‌پلی انجام می‌شود و شرایط قطع دائمی ارائه خدمات، تابع تعهدات اعلام‌شده هنگام خرید و حقوق قانونی مصرف‌کننده خواهد بود.",
        ],
      },
      {
        id: "pricing",
        title: "۴. قیمت‌ها، تخفیف‌ها و پرداخت",
        blocks: [
          {
            type: "ol",
            items: [
              "قیمت هر بازی یا اشتراک پیش از نهایی شدن خرید به کاربر نمایش داده می‌شود.",
              "تمامی پرداخت‌های نسخه ایرانی سایت از طریق روش‌ها و درگاه‌های پرداخت معرفی‌شده در وب‌سایت انجام می‌شود.",
              "مبلغ نهایی قابل پرداخت، پس از اعمال تخفیف‌های معتبر و هزینه‌های قانونی احتمالی، پیش از تأیید خرید نمایش داده خواهد شد.",
              "تخفیف‌ها، کدهای معرفی و پیشنهادهای ویژه تابع شرایط، محدودیت‌ها و مدت اعتبار اعلام‌شده برای همان طرح هستند.",
              "در صورت کسر مبلغ بدون ثبت موفق سفارش، کاربر می‌تواند موضوع را از طریق پشتیبانی پیگیری کند.",
              "هرگونه تغییر قیمت، بر خریدهایی که پیش از تغییر به‌طور قطعی انجام شده‌اند اثر نخواهد داشت؛ مگر مطابق شرایطی که پیش‌تر به کاربر اعلام شده و قانون اجازه می‌دهد.",
            ],
          },
        ],
      },
      {
        id: "activation",
        title: "۵. فعال‌سازی و ارائه خدمات",
        blocks: [
          {
            type: "ol",
            items: [
              "خدمات دیجیتال معمولاً پس از پرداخت موفق و تأیید تراکنش در حساب کاربر فعال می‌شوند.",
              "اطلاعات مربوط به خرید، نوع دسترسی و مدت اعتبار از طریق حساب کاربری قابل مشاهده خواهد بود.",
              "برای اجرای بازی‌ها، دسترسی به اینترنت، مرورگر سازگار و دستگاه مناسب ضروری است.",
              "کیفیت اتصال اینترنت و عملکرد دستگاه کاربران ممکن است بر تجربه بازی تأثیر بگذارد.",
              "در صورت بروز مشکل در فعال‌سازی خدمات، کاربر می‌تواند درخواست پشتیبانی ثبت کند.",
            ],
          },
        ],
      },
      {
        id: "renewal",
        title: "۶. تمدید و پایان اشتراک",
        blocks: [
          {
            type: "ol",
            items: [
              "اشتراک‌های مدت‌دار تا پایان دوره اعتبار خریداری‌شده فعال هستند.",
              "تمدید اشتراک در نسخه ایرانی صرفاً مطابق روش و شرایط اعلام‌شده در زمان خرید انجام می‌شود.",
              "در صورت فعال بودن تمدید خودکار برای هر طرح، این ویژگی، مبلغ تمدید، دوره پرداخت و روش لغو آن باید پیش از خرید به کاربر اعلام شود.",
              "پس از پایان اشتراک، دسترسی به بازی‌های مختص آن طرح متوقف می‌شود؛ اما دسترسی به بازی‌هایی که به‌صورت مستقل خریداری شده‌اند، مطابق شرایط خرید مستقل باقی خواهد ماند.",
            ],
          },
        ],
      },
      {
        id: "refunds",
        title: "۷. حق انصراف، لغو خرید و بازپرداخت وجه",
        blocks: [
          "مطابق ماده ۳۷ قانون تجارت الکترونیکی ایران، مصرف‌کنندگان در معاملات از راه دور اصولاً حداقل هفت روز کاری حق انصراف بدون جریمه و بدون نیاز به ارائه دلیل دارند.",
          "بر اساس ماده ۳۸ این قانون، در خدمات، مبدأ حق انصراف روز انعقاد قرارداد است؛ مشروط به ارائه اطلاعات الزامی قانونی به مصرف‌کننده.",
          "با این حال، مطابق بند «الف» ماده یک آیین‌نامه اجرایی مواد ۳۸ و ۴۲ قانون تجارت الکترونیکی، در خصوص خدماتی که ارائه آن‌ها با توافق مصرف‌کننده پیش از پایان هفت روز کاری آغاز شده باشد، استثنای قانونی حق انصراف اعمال می‌شود؛ مگر آنکه طرفین به نحو دیگری توافق کرده باشند.",
          "بنابراین:",
          {
            type: "ol",
            items: [
              "هنگام خرید خدماتی که بلافاصله فعال می‌شوند، موافقت صریح کاربر با شروع فوری ارائه خدمات دریافت خواهد شد.",
              "چنانچه خدمات با توافق صریح کاربر و در چهارچوب شرایط قانونی آغاز شده باشند، حق انصراف قانونی ممکن است مشمول استثنای مذکور شود.",
              "در مواردی که حق انصراف قانونی برقرار باشد، کاربر می‌تواند درخواست انصراف خود را از طریق راه‌های رسمی پشتیبانی اعلام کند و مبلغ دریافتی مطابق قانون مسترد خواهد شد.",
              "اگر وجه دریافت شود اما خدمات خریداری‌شده به دلیل مشکلی از سمت پاکت‌پلی ارائه نشود، کاربر حق درخواست رفع مشکل یا استرداد وجه مطابق قوانین مربوطه را خواهد داشت.",
              "در صورت توقف دائمی دسترسی خریداری‌شده به دلیل تصمیم یا مشکل منتسب به پاکت‌پلی، حقوق قراردادی و قانونی کاربران، از جمله استرداد وجه در موارد لازم، رعایت خواهد شد.",
              "شرایط انصراف و استرداد وجه با توجه به نوع خدمت، وضعیت فعال‌سازی و الزامات قانونی بررسی می‌شود.",
            ],
          },
          "هیچ‌یک از مفاد این بخش، حقوق الزامی و غیرقابل‌اسقاط مصرف‌کنندگان را محدود نمی‌کند.",
        ],
      },
      {
        id: "acceptable-use",
        title: "۸. شرایط استفاده از بازی‌ها",
        blocks: [
          {
            type: "ol",
            items: [
              "استفاده از بازی‌ها تنها در محدوده دسترسی و امکانات طرح خریداری‌شده مجاز است.",
              "کاربر مجاز به کپی‌برداری غیرمجاز، فروش مجدد دسترسی، دست‌کاری کدها یا تلاش برای دور زدن محدودیت‌های فنی نیست.",
              "استفاده از ابزارهای مخرب، ایجاد اختلال عمدی در جلسات دیگران و انجام فعالیت‌های غیرقانونی ممنوع است.",
              "در صورت نقض قوانین، پاکت‌پلی می‌تواند با رعایت تناسب و الزامات قانونی، دسترسی متخلف را محدود کند.",
              "استفاده تجاری یا برگزاری جلسات متعدد باید مطابق مجوزها و امکانات طرح خریداری‌شده، از جمله Venue، انجام شود.",
            ],
          },
        ],
      },
      {
        id: "service-changes",
        title: "۹. تغییرات بازی‌ها و خدمات",
        blocks: [
          "پاکت‌پلی ممکن است برای بهبود تجربه کاربران، بازی‌های جدید اضافه کند یا ویژگی‌های فنی بازی‌های موجود را به‌روزرسانی کند.",
          "تغییرات در فهرست بازی‌های اشتراکی باید با رعایت تعهدات اعلام‌شده هنگام خرید انجام شود.",
          "چنانچه تغییر یا حذف یک خدمت موجب عدم اجرای تعهدات مربوط به دسترسی خریداری‌شده شود، موضوع بر اساس شرایط قرارداد و حقوق قانونی مصرف‌کننده رسیدگی خواهد شد.",
        ],
      },
      {
        id: "intellectual-property",
        title: "۱۰. مالکیت معنوی",
        blocks: [
          "نام، نشان تجاری، طراحی وب‌سایت، نرم‌افزارها و محتوای اختصاصی پاکت‌پلی تحت حمایت قوانین مربوط به مالکیت فکری قرار دارند.",
          "خرید بازی یا اشتراک صرفاً مجوز استفاده از خدمات را مطابق شرایط اعلام‌شده به کاربر اعطا می‌کند و به معنای انتقال حقوق مالکیت معنوی نیست.",
        ],
      },
      {
        id: "privacy",
        title: "۱۱. حریم خصوصی و امنیت اطلاعات",
        blocks: [
          "پاکت‌پلی متعهد است اطلاعات کاربران را مطابق قوانین قابل‌اجرا و سیاست حریم خصوصی خود پردازش کند.",
          "اطلاعات حساب کاربری، تراکنش‌ها و داده‌های لازم برای ارائه خدمات صرفاً در چهارچوب اهداف اعلام‌شده و الزامات قانونی استفاده می‌شوند.",
        ],
      },
      {
        id: "liability",
        title: "۱۲. اختلالات فنی و مسئولیت‌ها",
        blocks: [
          "پاکت‌پلی تلاش می‌کند خدمات را با کیفیت و پایداری مناسب ارائه دهد.",
          "ممکن است به‌روزرسانی‌های فنی، اختلالات اینترنت یا شرایط خارج از کنترل معقول سرویس، موجب وقفه موقت شوند.",
          "در صورت بروز اختلال مؤثر در خدمات خریداری‌شده، کاربران می‌توانند موضوع را به پشتیبانی گزارش دهند.",
          "مسئولیت پاکت‌پلی در قبال عدم ارائه خدمات، اختلالات و خسارت‌های قابل مطالبه، مطابق قرارداد و قوانین لازم‌الاجرا تعیین می‌شود.",
        ],
      },
      {
        id: "changes",
        title: "۱۳. تغییر قوانین و مقررات",
        blocks: [
          "پاکت‌پلی می‌تواند برای تطبیق با تغییرات قانونی یا بهبود خدمات، این قوانین را به‌روزرسانی کند.",
          "تغییرات مؤثر بر حقوق یا تعهدات کاربران مطابق الزامات قانونی اطلاع‌رسانی خواهد شد.",
          "تغییرات جدید، حقوق قطعی ایجادشده بر اساس خریدهای قبلی را برخلاف قانون محدود نخواهد کرد.",
        ],
      },
      {
        id: "disputes",
        title: "۱۴. ثبت شکایت و حل اختلاف",
        blocks: [
          "کاربران می‌توانند درخواست‌ها و شکایت‌های خود را از طریق راه‌های ارتباطی رسمی پاکت‌پلی ثبت کنند.",
          "تلاش می‌شود مشکلات در مرحله نخست از طریق پشتیبانی و توافق با مشتری برطرف شوند.",
          "در صورت عدم حصول توافق، موضوع از طریق مراجع قانونی و صالح جمهوری اسلامی ایران قابل پیگیری است.",
        ],
      },
      {
        id: "contact",
        title: "۱۵. اطلاعات تماس و پشتیبانی",
        blocks: [
          {
            type: "fields",
            items: [
              { label: "نام تجاری", value: "پاکت‌پلی (PocketPlay)" },
              { label: "نام صاحب امتیاز", value: "سیدمحمدحسین فاطمی" },
              { label: "نشانی", value: "اتوبان صدر، خیابان قلندری شمالی، کوچه شهید معنوی" },
              { label: "کد پستی", value: "1931716654" },
              { label: "شماره تماس", value: "09051151251", href: "tel:09051151251" },
              { label: "ایمیل پشتیبانی", value: SUPPORT_EMAIL, href: `mailto:${SUPPORT_EMAIL}` },
            ],
          },
        ],
      },
    ],
    closing: [
      "با استفاده از خدمات پاکت‌پلی، کاربر تأیید می‌کند که اطلاعات و شرایط خرید را مطالعه کرده و با مفاد این قوانین، در حدود مجاز قانونی، موافقت دارد.",
    ],
  },
};
